import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { router } from '@inertiajs/react';
import Modal from '@/Components/Modal';
import { Button } from '@/Components/ui/button';

// A unified, reusable confirm dialog for all CRUD actions across the app.
// Usage:
//  const { confirm, withConfirm } = useConfirm();
//  - Simple boolean confirm:
//      const ok = await confirm({ intent: 'delete' });
//      if (ok) router.delete(...);
//  - Run action inside dialog (locks the Yes button while running):
//      await confirm({ intent: 'update', onConfirm: () => form.put(...) });
//  - Convenience wrapper:
//      const onClick = withConfirm(() => router.delete(...), { intent: 'delete' });

export type ConfirmIntent = 'create' | 'edit' | 'update' | 'delete';

export type ConfirmOptions = {
  // Optional explicit text overrides
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  // Intent defines default message
  intent?: ConfirmIntent;
  // When provided, the dialog will execute it and block the confirm button
  // while the promise is pending.
  onConfirm?: () => Promise<unknown> | unknown;
};

type InternalState = {
  open: boolean;
  opts: ConfirmOptions | null;
  processing: boolean;
  resolve?: (value: boolean) => void;
};

type Ctx = {
  // Ask for confirmation; if onConfirm provided, it will be executed inside.
  ask: (opts: ConfirmOptions) => Promise<boolean>;
};

const ConfirmCtx = createContext<Ctx | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');

  const confirm = ctx.ask;

  const withConfirm = useCallback(<A extends any[]>(
    fn: (...args: A) => Promise<unknown> | unknown,
    opts: ConfirmOptions
  ) => {
    return async (...args: A) => {
      await confirm({ ...opts, onConfirm: () => fn(...args) });
    };
  }, [confirm]);

  return { confirm, withConfirm };
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<InternalState>({ open: false, opts: null, processing: false });

  const defaults = useMemo(() => ({
    // Default texts in Russian as required
    messagesByIntent: {
      delete: 'Вы действительно хотите удалить этот элемент?',
      create: 'Создать новый элемент?',
      edit: 'Сохранить изменения?',
      update: 'Сохранить изменения?',
    } as Record<ConfirmIntent, string>,
    confirmText: 'Да',
    cancelText: 'Нет',
    title: 'Подтверждение',
  }), []);

  const ask = useCallback<Ctx['ask']>((opts) => {
    try { console.info('[ConfirmDialog] open'); } catch {}
    return new Promise<boolean>((resolve) => {
      setState({ open: true, opts, processing: false, resolve });
    });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    try { console.info('[ConfirmDialog] close'); } catch {}
    // Do not resolve here; resolution happens explicitly on actions
  }, []);

  const onCancel = useCallback(() => {
    if (state.resolve) state.resolve(false);
    close();
  }, [state.resolve, close]);

  const onConfirm = useCallback(async () => {
    if (!state.opts) return;
    const run = state.opts.onConfirm;
    // If no action provided — simple confirm
    if (!run) {
      if (state.resolve) state.resolve(true);
      close();
      return;
    }

    // Close the modal immediately to avoid full-screen overlay during network ops
    close();
    try {
      await Promise.resolve(run());
      if (state.resolve) state.resolve(true);
    } catch (e) {
      // swallow — errors will be surfaced by the action itself (toasts/validation)
    }
  }, [state.opts, state.resolve, close]);

  // Safety net: ensure any navigation closes the dialog overlay
  useEffect(() => {
    const unStart = router.on('start', () => setState((s) => ({ ...s, open: false })));
    const unError = router.on('error', () => setState((s) => ({ ...s, open: false })));
    const unFinish = router.on('finish', () => setState((s) => ({ ...s, open: false })));
    return () => { unStart(); unError(); unFinish(); };
  }, []);

  const message = useMemo(() => {
    const { opts } = state;
    if (!opts) return '';
    if (opts.message) return opts.message;
    if (opts.intent) return defaults.messagesByIntent[opts.intent];
    return defaults.messagesByIntent.update;
  }, [state, defaults]);

  const title = state.opts?.title ?? defaults.title;
  const confirmText = state.opts?.confirmText ?? defaults.confirmText;
  const cancelText = state.opts?.cancelText ?? defaults.cancelText;

  return (
    <ConfirmCtx.Provider value={{ ask }}>
      {children}
      <Modal
        show={state.open}
        onClose={state.processing ? () => {} : close}
        zIndexClass="z-[1200]"
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-2">{title}</h2>
          <p className="text-sm text-gray-700 mb-4">{message}</p>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={onCancel}
              disabled={state.processing}
            >
              {cancelText}
            </Button>
            <Button
              variant="default"
              type="button"
              onClick={onConfirm}
              disabled={state.processing}
            >
              {state.processing ? 'Подождите…' : confirmText}
            </Button>
          </div>
        </div>
      </Modal>
    </ConfirmCtx.Provider>
  );
}
